import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { BotidService } from '../../ragai/botid.service';

type GammaInputOptions = {
  inputText: string;
  textMode: 'generate' | 'condense' | 'preserve';
  format?: 'presentation' | 'document' | 'social';
  themeId?: string;
  numCards?: number;
  cardSplit?: 'auto' | 'inputTextBreaks';
  additionalInstructions?: string;
  exportAs?: 'pdf' | 'pptx';
  folderIds?: string[];
  textOptions?: {
    amount?: 'brief' | 'medium' | 'detailed' | 'extensive',
    tone?: string,
    audience?: string,
    language?: string
  },
  imageOptions?: {
    source?: 'aiGenerated' | 'pictographic' | 'unsplash' | 'webAllImages' | 'webFreeToUse' | 'webFreeToUseCommercially' | 'giphy' | 'placeholder' | 'noImages',
    model?: string,
    style?: string
  },
  cardOptions?: {
    dimensions?: string,
    headerFooter?: {
      hideFromFirstCard?: boolean,
      hideFromLastCard?: boolean,
      topLeft?: HeaderFooterOption,
      topCenter?: HeaderFooterOption,
      topRight?: HeaderFooterOption,
      bottomLeft?: HeaderFooterOption,
      bottomCenter?: HeaderFooterOption,
      bottomRight?: HeaderFooterOption
    }
  },
  sharingOptions?: {
    workspaceAccess?: 'noAccess' | 'view' | 'comment' | 'edit' | 'fullAccess',
    externalAccess?: 'noAccess' | 'view' | 'comment' | 'edit',
    emailOptions?: {
      recipients: string[];
      access: 'view' | 'comment' | 'edit' | 'fullAccess';
    }
  }
};

type HeaderFooterOption = {
  type: 'cardNumber' | 'image' | 'text',
  value?: string,
  source?: 'themeLogo' | 'custom',
  src?: string,
  size?: 'sm' | 'md' | 'lg' | 'xl'
};

/**
 * Erzeugt Präsentationen/Dokumente über die Gamma.app-API (`RAGAI.GAMMA`).
 * @remarks
 * {@link generate} clamped mehrere Felder serverseitig-kompatibel, statt Eingaben zurückzuweisen:
 * `inputText` wird auf 750.000 Zeichen gekappt, `numCards` auf den Bereich 1–50 begrenzt,
 * `additionalInstructions` auf 2.000 Zeichen sowie `textOptions.tone`/`.audience` und `imageOptions.style` auf
 * je 500 Zeichen gekürzt. Zu lange Eingaben werden also **stillschweigend** abgeschnitten, nicht per Fehler abgelehnt.
 */
@Injectable({
  providedIn: 'root'
})
export class GammaService {
  constructor(
    private http: HttpClient,
    private botIdService: BotidService
  ) { }

  /** Erzeugt eine Gamma.app-Präsentation/Dokument aus den übergebenen Optionen (überlange Felder werden vorher automatisch gekürzt). */
  public async generate(options: GammaInputOptions) {
    options.inputText = options.inputText.substring(0, 750000);

    if (options.numCards) {
      options.numCards = Math.round(options.numCards);
      if (options.numCards < 1) {
        options.numCards = 1;
      } else if (options.numCards > 50) {
        options.numCards = 50;
      }
    }

    if (options.additionalInstructions) {
      options.additionalInstructions = options.additionalInstructions.substring(0, 2000);
    }

    if (options.textOptions?.tone) {
      options.textOptions.tone = options.textOptions.tone.substring(0, 500);
    }

    if (options.textOptions?.audience) {
      options.textOptions.audience = options.textOptions.audience.substring(0, 500);
    }

    if (options.imageOptions?.style) {
      options.imageOptions.style = options.imageOptions.style.substring(0, 500);
    }

    return await firstValueFrom(this.http.post<string>('/api/user/gamma/' + this.botIdService.getBotId() + '/generate/', { gammaOptions: options }));
  }
}